import { Text, View } from '@react-pdf/renderer'
import { AutoComplete, DatePicker, Form, Input, Select, Space } from 'antd'
import { CantileverButton } from 'components/CantileverButton'
import dayjs from 'dayjs'
import GeckoLogo from 'features/reporting/pdfGeneration/components/shared/GeckoLogo'
import { GradientStripe } from 'features/reporting/pdfGeneration/components/shared/GradientStripe'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { ReportPage } from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { PDFStyles } from 'features/reporting/pdfGeneration/styles/PDFStyles'
import { PdfProps, ReportSection } from 'features/reporting/types'
import {
  CoverSectionData,
  DEFAULT_INSPECTION_TEMPERATURE_UNIT,
  inspectionTemperatureUnits,
  MainInfo,
  RugReportSectionId,
} from 'pages/reporting/reports/rug/rugReportData'
import { useRugReportOps } from 'pages/reporting/reports/rug/RugReportProvider'
import {
  ReportedAssetType,
  ReportMaterialType,
  XAxisStartLocationOptions,
  YAxisStartLocationOptions,
} from 'pages/reporting/reports/rug/types'
import {
  normalizeInspectionTemperature,
  serializeInspectionTemperatureForForm,
} from 'pages/reporting/reports/rug/utils/inspectionTemperature'
import { StyledForm } from 'pages/reporting/shared/StyledComponents'
import React, { useMemo } from 'react'
import { SignoffState } from 'types'
import { autocompleteFilterOption } from 'utils/fde/antD'

const REPORT_DATE_FORMAT = 'MM/DD/YYYY'
export const RUG_COVER_HEADER_TEXT = 'Ultrasonics Inspection Report'

export const RUG_MANUAL_SIGNOFF_ROWS = [
  'Field Technician',
  'Data Analyst',
  'Reviewed By',
] as const

export const getRugManualContributorRows = (signoff?: SignoffState) => [
  {
    contributorType: RUG_MANUAL_SIGNOFF_ROWS[0],
    contributor: {
      name: signoff?.supervisorSignoff?.name || '',
      certification: signoff?.supervisorSignoff?.qualifications || '',
    },
  },
  {
    contributorType: RUG_MANUAL_SIGNOFF_ROWS[1],
    contributor: {
      name: signoff?.dataAnalystSignoff?.name || '',
      certification: signoff?.dataAnalystSignoff?.qualifications || '',
    },
  },
  {
    contributorType: RUG_MANUAL_SIGNOFF_ROWS[2],
    contributor: {
      name: signoff?.reviewerSignoff?.name || '',
      certification: signoff?.reviewerSignoff?.qualifications || '',
    },
  },
]

const INSPECTION_TEMPERATURE_UNIT_OPTIONS = inspectionTemperatureUnits.map(
  (unit) => ({
    label: unit.name,
    value: unit.id,
  })
)

const requiredCoverPageFields = [
  'mainInfo.customerName',
  'mainInfo.customerLocation',
  'mainInfo.assetName',
  'mainInfo.assetType',
  'mainInfo.assetId',
  'coverSectionData.inspectionStartDate',
  'coverSectionData.inspectionEndDate',
]

const ContributorRow: React.FC<{
  contributorType: string
  contributor?: {
    name?: string
    certification?: string
  }
}> = ({ contributorType, contributor }) => {
  return (
    <>
      <View style={PDFStyles.contributorRow}>
        <Text style={PDFStyles.contributorHeaderText}>{contributorType}</Text>
        <Text style={PDFStyles.contributorHeaderText}>Certification</Text>
        <Text style={PDFStyles.contributorHeaderText}>Date</Text>
        <Text style={PDFStyles.contributorHeaderText}>Signature</Text>
      </View>
      <View style={PDFStyles.contributorBox}>
        <Text style={PDFStyles.contributorText}>{contributor?.name || ''}</Text>
        <Text style={PDFStyles.contributorText}>
          {contributor?.certification || ''}
        </Text>
        <Text style={PDFStyles.contributorText}></Text>
        <Text style={PDFStyles.contributorText}></Text>
      </View>
    </>
  )
}

export const RugCoverPDF: React.FC<PdfProps> = ({ onRender }) => {
  const { formData } = useRugReportOps()

  const signoff = useMemo(() => formData.signoff, [formData.signoff])
  const mainInfo = useMemo(() => formData.mainInfo, [formData.mainInfo])
  const coverData = useMemo(
    () => formData.coverSectionData,
    [formData.coverSectionData]
  )

  const oneDayInspection = useMemo(() => {
    const startDate = dayjs(coverData.inspectionStartDate)
    const endDate = dayjs(coverData.inspectionEndDate)
    return startDate.isSame(endDate, 'day')
  }, [coverData.inspectionStartDate, coverData.inspectionEndDate])

  const methodTitle = 'Rapid Ultrasonic Gridding (RUG) Examination' // RUG specific

  const getDatesOfInspectionTitle = () => {
    if (!coverData.inspectionStartDate || !coverData.inspectionEndDate) {
      return 'Not specified'
    }

    const startDate = dayjs(coverData.inspectionStartDate).format(
      REPORT_DATE_FORMAT
    )
    const endDate = dayjs(coverData.inspectionEndDate).format(
      REPORT_DATE_FORMAT
    )

    if (oneDayInspection) {
      return startDate
    }

    return `${startDate} - ${endDate}`
  }

  return (
    <ReportPage
      id='cover'
      wrap={false}
      tracker={{
        sectionId: RugReportSectionId.COVER,
        onRender,
        registerOnce: false,
      }}
      contentStyle={PDFStyles.content}
      footer={
        <PageFooter
          jobNumber={mainInfo.assetId}
          revisionNumber={formData.revisionMetadata.revisionNumber}
        />
      }>
      {/* Header with Gecko logo and gradient stripe */}
      <View style={PDFStyles.coverHeader}>
        <GeckoLogo />
        <View style={PDFStyles.coverHeaderContent}>
          <Text style={PDFStyles.coverHeaderText}>{RUG_COVER_HEADER_TEXT}</Text>
          <GradientStripe />
        </View>
      </View>

      {/* Customer Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>Customer</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerName}
          </Text>
          <Text style={PDFStyles.coverPageValueTextLarge}>
            {mainInfo.customerLocation}
          </Text>
        </View>
      </View>

      {/* Method Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Method</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{methodTitle}</Text>
        </View>
      </View>

      {/* Asset Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageBoldTitleText}>Asset</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {`${mainInfo.assetName} - ${mainInfo.assetType}`}
          </Text>
        </View>
      </View>

      {/* Job Number Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageShortDash} />
          <Text style={PDFStyles.coverPageTitleText}>Gecko Job Number</Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>{mainInfo.assetId}</Text>
        </View>
      </View>

      {/* Inspection Dates Section */}
      <View style={PDFStyles.coverPageSectionContainer}>
        <View style={PDFStyles.coverPageSectionTitle}>
          <View style={PDFStyles.coverPageDash} />
          <Text style={PDFStyles.coverPageTitleText}>
            {oneDayInspection ? 'Date of Inspection' : 'Dates of Inspection'}
          </Text>
        </View>
        <View style={PDFStyles.coverPageSectionContent}>
          <Text style={PDFStyles.coverPageValueText}>
            {getDatesOfInspectionTitle()}
          </Text>
        </View>
      </View>

      {/* Signature Section */}
      <View style={PDFStyles.signatureSection}>
        {getRugManualContributorRows(signoff).map(
          ({ contributorType, contributor }) => (
            <ContributorRow
              key={contributorType}
              contributorType={contributorType}
              contributor={contributor}
            />
          )
        )}
      </View>
    </ReportPage>
  )
}

const RenderPDF = RugCoverPDF

interface CoverPageFormValues
  extends
    Omit<MainInfo, 'componentAxisStartLocations' | 'inspectionTemperature'>,
    Omit<CoverSectionData, 'inspectionStartDate' | 'inspectionEndDate'> {
  inspectionTemperature?: {
    value?: string
    unit?: string
  }
  inspectionStartDate: dayjs.Dayjs
  inspectionEndDate: dayjs.Dayjs
  [key: `xAxis_${string}` | `yAxis_${string}`]: string | undefined
}

const RenderReact: React.FC = () => {
  const [form] = Form.useForm<CoverPageFormValues>()
  const {
    formData,
    handleCoverSectionFinish,
    handleMainInfoFinish,
    selectedAssetModel,
    selectedComponents,
  } = useRugReportOps()

  const initialValues = React.useMemo(() => {
    const componentAxisValues: Record<string, string> = {}
    if (formData.mainInfo.componentAxisStartLocations) {
      Object.entries(formData.mainInfo.componentAxisStartLocations).forEach(
        ([componentId, locations]) => {
          componentAxisValues[`xAxis_${componentId}`] =
            locations.xAxisStartLocation
          componentAxisValues[`yAxis_${componentId}`] =
            locations.yAxisStartLocation
        }
      )
    }

    return {
      ...formData.mainInfo,
      inspectionTemperature: serializeInspectionTemperatureForForm(
        formData.mainInfo.inspectionTemperature
      ) ?? {
        unit: DEFAULT_INSPECTION_TEMPERATURE_UNIT.id,
      },
      ...componentAxisValues,
      inspectionStartDate: formData.coverSectionData.inspectionStartDate
        ? dayjs(formData.coverSectionData.inspectionStartDate)
        : undefined,
      inspectionEndDate: formData.coverSectionData.inspectionEndDate
        ? dayjs(formData.coverSectionData.inspectionEndDate)
        : undefined,
    }
  }, [formData.mainInfo, formData.coverSectionData])

  React.useEffect(() => {
    form.setFieldsValue(initialValues)
  }, [form, initialValues])

  const onFinish = async (values: CoverPageFormValues) => {
    const componentAxisStartLocations: Record<
      string,
      { xAxisStartLocation: string; yAxisStartLocation: string }
    > = {}

    selectedComponents?.forEach((component) => {
      const xKey = `xAxis_${component.id}` as const
      const yKey = `yAxis_${component.id}` as const
      if (values[xKey] !== undefined || values[yKey] !== undefined) {
        componentAxisStartLocations[component.id] = {
          xAxisStartLocation: values[xKey] || '',
          yAxisStartLocation: values[yKey] || '',
        }
      }
    })

    const mainInfoUpdate: MainInfo = {
      assetName: values.assetName,
      assetId: values.assetId,
      assetType: values.assetType,
      assetMaterial: values.assetMaterial,
      customerName: values.customerName,
      customerLocation: values.customerLocation,
      inspectionTemperature: normalizeInspectionTemperature(
        values.inspectionTemperature?.value,
        values.inspectionTemperature?.unit
      ),
      componentAxisStartLocations,
    }

    const coverData: CoverSectionData = {
      inspectionStartDate:
        values.inspectionStartDate.format(REPORT_DATE_FORMAT),
      inspectionEndDate: values.inspectionEndDate.format(REPORT_DATE_FORMAT),
    }

    await handleCoverSectionFinish(coverData)
    await handleMainInfoFinish(mainInfoUpdate)
  }

  return (
    <StyledForm
      form={form}
      name='rugCoverPageForm'
      onFinish={onFinish}
      layout='vertical'
      initialValues={initialValues}>
      <Form.Item name='customerName' label='Customer Name'>
        <Input />
      </Form.Item>

      <Form.Item name='customerLocation' label='Customer Location'>
        <Input />
      </Form.Item>

      <Form.Item name='assetName' label='Asset Name'>
        <Input />
      </Form.Item>

      <Form.Item name='assetType' label='Asset Type'>
        <AutoComplete
          options={Object.values(ReportedAssetType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Type'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item name='assetId' label='Gecko Job Number'>
        <Input />
      </Form.Item>

      <Form.Item name='assetMaterial' label='Asset Material'>
        <AutoComplete
          options={Object.values(ReportMaterialType).map((status) => {
            return { value: status }
          })}
          placeholder='Select Asset Material'
          filterOption={autocompleteFilterOption}
        />
      </Form.Item>

      <Form.Item label='Inspection Temperature'>
        <Space.Compact style={{ width: '100%' }}>
          <Form.Item name={['inspectionTemperature', 'value']} noStyle>
            <Input placeholder='e.g., 83 or Ambient' />
          </Form.Item>
          <Form.Item name={['inspectionTemperature', 'unit']} noStyle>
            <Select
              style={{ width: '96px' }}
              options={INSPECTION_TEMPERATURE_UNIT_OPTIONS}
            />
          </Form.Item>
        </Space.Compact>
      </Form.Item>

      {selectedComponents && selectedComponents.length > 0 && (
        <>
          {selectedComponents.map((component) => {
            const assetType = selectedAssetModel?.data.type

            return (
              <React.Fragment key={component.id}>
                <Form.Item
                  name={`xAxis_${component.id}`}
                  label={`X-Axis Start Location (${component.data.name})`}>
                  <AutoComplete
                    options={(assetType
                      ? (XAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOption}
                  />
                </Form.Item>
                <Form.Item
                  name={`yAxis_${component.id}`}
                  label={`Y-Axis Start Location (${component.data.name})`}>
                  <AutoComplete
                    options={(assetType
                      ? (YAxisStartLocationOptions[assetType] ?? [])
                      : []
                    ).map((option) => ({ value: option }))}
                    placeholder='Select or Type Location'
                    filterOption={autocompleteFilterOption}
                  />
                </Form.Item>
              </React.Fragment>
            )
          })}
        </>
      )}

      <Form.Item name='inspectionStartDate' label='Start Date'>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item name='inspectionEndDate' label='End Date'>
        <DatePicker style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item>
        <CantileverButton
          primary
          onClick={() => form.submit()}
          style={{ width: '100%' }}>
          Save Cover Page
        </CantileverButton>
      </Form.Item>
    </StyledForm>
  )
}

export const Cover: ReportSection = {
  RenderReact,
  required: true,
  RenderPDF,
  id: RugReportSectionId.COVER,
  description: 'Basic report information and metadata',
  title: 'Cover Page',
  requiredFields: requiredCoverPageFields,
}
